import { progressFetch } from '../helpers/progress-fetch.js'
import { t } from '../helpers/i18n.js'
import { flash, i18nFlash } from '../helpers/flash.js'
import { apiUrl } from '../helpers/api.js'
import { handleApiError } from '../helpers/handle-api-error.js'
import router from '../router.js'

export default {
  namespaced: true,

  state: {
    project: null,
    originalProject: null, // For dirty checking in edit mode
    phases: [],
    appointments: [],
    partners: [],
    mode: null, // 'view' or 'edit'
    loading: false,
    saving: false,
    savingPhase: null,
    savingAppointment: null,
    errors: []
  },

  getters: {
    projectLoaded: state => state.project !== null,

    isEditMode: state => state.mode === 'edit',

    isViewMode: state => state.mode === 'view',

    // Form getters
    isDirty: state => {
      if (!state.project || !state.originalProject) return false
      const addressFields = [
        'property_number', 'name', 'name2', 'street', 'zip', 'city',
        'country', 'telephone', 'cellphone', 'email', 'c6', 'channel'
      ]
      const phaseFields = ['order_type', 'note', 'info1']

      const address = state.project.address || {}
      const originalAddress = state.originalProject.address || {}

      for (const field of addressFields) {
        if (address[field] !== originalAddress[field]) {
          return true
        }
      }

      const userIds = state.project.user_ids || []
      const originalUserIds = state.originalProject.user_ids || []
      if (userIds.length !== originalUserIds.length) return true
      for (let i = 0; i < userIds.length; i++) {
        if (userIds[i] !== originalUserIds[i]) return true
      }

      if (state.project.phase && state.originalProject.phase) {
        for (const pField of phaseFields) {
          if (state.project.phase[pField] !== state.originalProject.phase[pField]) {
            return true
          }
        }
      }

      return false
    },

    isNewRecord: state => state.project && !state.project.persisted,

    // Detail getters
    phaseById: state => id => state.phases.find(p => p.id === id),

    appointmentById: state => id => state.appointments.find(a => a.id === id),

    partnerByRole: state => role => state.partners.find(p => p.role === role),

    assembler: (state, getters) => getters.partnerByRole('assembler'),

    customer: (state, getters) => getters.partnerByRole('customer'),

    invoiceRecipient: (state, getters) => getters.partnerByRole('invoice_recipient') || getters.partnerByRole('payer'),

    visiblePhases: state => state.phases.filter(p => p.can_show),

    currentPhase: state => {
      const nonInternal = state.phases.filter(p => !p.internal)
      return nonInternal[nonInternal.length - 1]
    }
  },

  mutations: {
    setProject(state, data) {
      state.project = data
      state.originalProject = JSON.parse(JSON.stringify(data))
      state.phases = data.phases || []
      state.appointments = data.appointments || []
      state.partners = data.partners || []
    },

    setMode(state, mode) {
      state.mode = mode
    },

    updateProject(state, updates) {
      Object.assign(state.project, updates)
    },

    updateField(state, { field, value }) {
      state.project[field] = value
    },

    updateAddressField(state, { field, value }) {
      if (!state.project.address) {
        state.project.address = {}
      }
      state.project.address[field] = value
    },

    updatePhaseField(state, { field, value }) {
      if (state.project.phase) {
        state.project.phase[field] = value
      }
    },

    setLoading(state, loading) {
      state.loading = loading
    },

    setSaving(state, saving) {
      state.saving = saving
    },

    setErrors(state, errors) {
      state.errors = errors
    },

    clearErrors(state) {
      state.errors = []
    },

    markClean(state) {
      state.originalProject = JSON.parse(JSON.stringify(state.project))
    },

    resetState(state) {
      state.project = null
      state.originalProject = null
      state.phases = []
      state.appointments = []
      state.partners = []
      state.mode = null
      state.loading = false
      state.saving = false
      state.savingPhase = null
      state.savingAppointment = null
      state.errors = []
    },

    // Phase mutations
    updatePhase(state, phase) {
      const index = state.phases.findIndex(p => p.id === phase.id)
      if (index !== -1) {
        state.phases[index] = phase
      }
    },

    addPhase(state, phase) {
      state.phases.push(phase)
    },

    removePhase(state, phaseId) {
      const index = state.phases.findIndex(p => p.id === phaseId)
      if (index !== -1) {
        state.phases.splice(index, 1)
      }
    },

    setSavingPhase(state, phaseId) {
      state.savingPhase = phaseId
    },

    // Appointment mutations
    updateAppointment(state, appointment) {
      const index = state.appointments.findIndex(a => a.id === appointment.id)
      if (index !== -1) {
        state.appointments[index] = appointment
      }
    },

    setSavingAppointment(state, appointmentId) {
      state.savingAppointment = appointmentId
    }
  },

  actions: {
    async fetchProject({ commit }, payload) {
      commit('setLoading', true)
      commit('clearErrors')

      let url
      const id = typeof payload === 'object' ? payload.id : payload
      const partnerId = typeof payload === 'object' ? payload.partnerId : null

      if (id) {
        url = apiUrl(`/projects/${id}`)
      } else {
        url = apiUrl('/projects/new')
        if (partnerId) {
          url += `?partner_id=${partnerId}`
        }
      }

      try {
        const response = await progressFetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        })
        if (await handleApiError(response)) {
          return
        }
        if (!response.ok) {
          throw new Error('Failed to load project')
        }
        const data = await response.json()
        commit('setProject', data)
        commit('setMode', data.show_edit_mode ? 'edit' : 'view')
        return data
      } catch (error) {
        commit('setErrors', [error.toString()])
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    // Form actions
    async saveProject({ state, commit, rootState }, payload) {
      commit('setSaving', true)
      commit('clearErrors')

      const { project } = state
      const isNew = !project.persisted
      const link = isNew ? project.links?.create : project.links?.update

      if (!link) {
        commit('setSaving', false)
        throw new Error('No save link available')
      }

      const address = project.address || {}
      const body = {
        project: {
          property_number: address.property_number,
          name: address.name,
          name2: address.name2,
          street: address.street,
          zip: address.zip,
          city: address.city,
          country: address.country,
          telephone: address.telephone,
          cellphone: address.cellphone,
          email: address.email,
          c6: address.c6,
          channel: address.channel,
          user_ids: project.user_ids || [],
          phases_attributes: [{
            id: project.phase?.id ?? null,
            order_type: project.phase?.order_type ?? null,
            note: project.phase?.note ?? null,
            info1: project.phase?.info1 ?? null
          }]
        }
      }

      if (payload?.addressId) {
        body.address_id = payload.addressId
        body.set_address = true
      }

      try {
        const response = await progressFetch(`${link.href}`, {
          method: link.method ? link.method.toUpperCase() : (isNew ? 'POST' : 'PATCH'),
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': rootState.app.csrfToken ?? ''
          },
          body: JSON.stringify(body)
        })
        if (!response.ok) {
          const errorData = await response.json()
          throw errorData.errors || ['Save failed']
        }
        const data = await response.json()
        commit('setProject', data)
        commit('setMode', data.show_edit_mode ? 'edit' : 'view')
        if (isNew && data.id) {
          router.push({ name: 'project-show', params: { id: data.id } })
        } else {
          i18nFlash('vue.pages.project_form.save_success', 'success')
        }
        return data
      } catch (errors) {
        const errorList = Array.isArray(errors) ? errors : [errors.toString()]
        commit('setErrors', errorList)
        throw errors
      } finally {
        commit('setSaving', false)
      }
    },

    async deleteProject({ state, commit, rootState }) {
      const { project } = state
      if (!project.links?.destroy) throw new Error('Cannot delete')

      commit('setSaving', true)

      try {
        const response = await progressFetch(`${project.links.destroy.href}`, {
          method: 'DELETE',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': rootState.app.csrfToken ?? ''
          }
        })
        if (!response.ok) {
          throw new Error('Delete failed')
        }
        const messageKey = state.mode === 'edit'
          ? 'vue.pages.project_form.delete_success'
          : 'vue.pages.project_detail.destroy_success'
        i18nFlash(messageKey, 'success')
      } catch (error) {
        commit('setErrors', [error.toString()])
        i18nFlash('vue.errors.delete_failed', 'alert')
        throw error
      } finally {
        commit('setSaving', false)
      }
    },

    async restoreProject({ state, commit, rootState }) {
      const { project } = state
      if (!project.links?.restore) throw new Error('Cannot restore')

      commit('setSaving', true)

      try {
        const response = await progressFetch(`${project.links.restore.href}`, {
          method: 'PATCH',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': rootState.app.csrfToken ?? ''
          }
        })
        if (!response.ok) {
          throw new Error('Restore failed')
        }
        const messageKey = state.mode === 'edit'
          ? 'vue.pages.project_form.restore_success'
          : 'vue.pages.project_detail.restore_success'
        i18nFlash(messageKey, 'success')
      } catch (error) {
        commit('setErrors', [error.toString()])
        i18nFlash('vue.errors.restore_failed', 'alert')
        throw error
      } finally {
        commit('setSaving', false)
      }
    },

    async transitionInquiryState({ state, commit, dispatch, rootState }, { action }) {
      const { inquiry } = state.project
      if (!inquiry?.links) {
        throw new Error('No inquiry or links')
      }

      const link = inquiry.links[action]
      if (!link) {
        throw new Error(`Cannot ${action}`)
      }

      commit('setSaving', true)

      try {
        const response = await progressFetch(`${link.href}`, {
          method: link.method ? link.method.toUpperCase() : 'PATCH',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': rootState.app.csrfToken ?? ''
          }
        })
        const body = await response.json().catch(() => ({}))
        if (!response.ok) {
          const errors = body.errors?.length ? body.errors : ['Transition failed']
          commit('setErrors', errors)
          throw new Error(errors.join(', '))
        }
        return dispatch('fetchProject', { id: state.project.id })
      } catch (error) {
        if (!state.errors?.length) {
          commit('setErrors', [error.toString()])
        }
        throw error
      } finally {
        commit('setSaving', false)
      }
    },

    // Detail actions
    async updatePhaseInline({ getters, commit, rootState }, { phaseId, field, value }) {
      const phase = getters.phaseById(phaseId)
      if (!phase?.links) throw new Error('Phase not found')

      const link = phase.new_record ? phase.links.create : phase.links.update
      if (!link) throw new Error('No link available')

      commit('setSavingPhase', phaseId)

      const paramKey = `phase_${phase.type_key}`
      const body = {
        [paramKey]: {
          [field]: value
        }
      }

      try {
        const response = await progressFetch(`${link.href}`, {
          method: link.method.toUpperCase(),
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': rootState.app.csrfToken ?? ''
          },
          body: JSON.stringify(body)
        })
        if (response.ok) {
          const updatedPhase = await response.json()
          commit('updatePhase', updatedPhase)
          i18nFlash('vue.components.phase_row.update_success', 'success')
        } else {
          const data = await response.json()
          flash(data.message || data.errors.join(', '), 'alert')
        }
      } catch {
        i18nFlash('vue.errors.update_failed', 'alert')
      } finally {
        commit('setSavingPhase', null)
      }
    },

    async updateAppointmentField({ getters, commit, rootState }, { appointmentId, field, value }) {
      const appointment = getters.appointmentById(appointmentId)
      if (!appointment?.links?.update) throw new Error('Appointment not found')

      commit('setSavingAppointment', appointmentId)

      const body = { appointment: { [field]: value } }

      try {
        const response = await progressFetch(`${appointment.links.update.href}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': rootState.app.csrfToken ?? ''
          },
          body: JSON.stringify(body)
        })
        if (response.ok) {
          const updatedAppointment = await response.json()
          commit('updateAppointment', updatedAppointment)
          i18nFlash('vue.components.appointment_card.update_success', 'success')
        } else {
          const data = await response.json()
          flash(data.errors?.join(', ') ?? t('vue.errors.update_failed'), 'alert')
        }
      } catch {
        i18nFlash('vue.errors.update_failed', 'alert')
      } finally {
        commit('setSavingAppointment', null)
      }
    },

    async updateUserIds({ state, commit, rootState }, userIds) {
      const { project } = state
      if (!project.links?.update_users) throw new Error('Cannot update users')

      commit('setSaving', true)

      try {
        const response = await progressFetch(project.links.update_users.href, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': rootState.app.csrfToken ?? ''
          },
          body: JSON.stringify({ project: { user_ids: userIds } })
        })
        if (response.ok) {
          const data = await response.json()
          commit('updateProject', { user_ids: data.user_ids })
          i18nFlash('vue.pages.project_detail.update_users_success', 'success')
        } else {
          const data = await response.json()
          flash(data.errors?.join(', ') ?? t('vue.errors.update_failed'), 'alert')
        }
      } catch {
        i18nFlash('vue.errors.update_failed', 'alert')
      } finally {
        commit('setSaving', false)
      }
    },

    async updateUser({ state, commit, rootState }, userId) {
      const { project } = state
      if (!project.links?.update_user) throw new Error('Cannot update user')

      commit('setSaving', true)

      try {
        const response = await progressFetch(project.links.update_user.href, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': rootState.app.csrfToken ?? ''
          },
          body: JSON.stringify({ project: { user_id: userId } })
        })
        if (response.ok) {
          const data = await response.json()
          commit('updateProject', { user: data.user })
          i18nFlash('vue.pages.project_detail.update_user_success', 'success')
        } else {
          const data = await response.json()
          flash(data.errors?.join(', ') ?? t('vue.errors.update_failed'), 'alert')
        }
      } catch {
        i18nFlash('vue.errors.update_failed', 'alert')
      } finally {
        commit('setSaving', false)
      }
    },

    async requestCheck({ state, commit, rootState }, { finished }) {
      const { project } = state
      if (!project.links?.request_check) throw new Error('Cannot request check')

      commit('setSaving', true)

      try {
        const response = await progressFetch(project.links.request_check.href, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': rootState.app.csrfToken ?? ''
          },
          body: JSON.stringify({ finished: finished ? 'true' : 'false' })
        })
        if (response.ok) {
          const data = await response.json()
          commit('updateProject', {
            requested_check_at: data.requested_check_at,
            requested_check_by: data.requested_check_by
          })
          i18nFlash('vue.pages.project_detail.request_check_success', 'success')
        } else {
          const data = await response.json()
          let key = 'vue.errors.request_failed'
          if (data.needs_documents) {
            key = 'vue.pages.project_detail.needs_documents'
          } else if (data.needs_appointment) {
            key = 'vue.pages.project_detail.needs_appointment'
          }
          i18nFlash(key, 'alert')
        }
      } catch {
        i18nFlash('vue.errors.request_failed', 'alert')
      } finally {
        commit('setSaving', false)
      }
    },

    async markChecked({ state, commit, rootState }) {
      const { project } = state
      if (!project.links?.checked) throw new Error('Cannot mark checked')

      commit('setSaving', true)

      try {
        const response = await progressFetch(project.links.checked.href, {
          method: 'PATCH',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': rootState.app.csrfToken ?? ''
          }
        })
        if (response.ok) {
          const data = await response.json()
          commit('updateProject', {
            checked_at: data.checked_at,
            requested_check_at: null,
            requested_check_by: null
          })
          i18nFlash('vue.pages.project_detail.checked_success', 'success')
        } else {
          i18nFlash('vue.errors.update_failed', 'alert')
        }
      } catch {
        i18nFlash('vue.errors.update_failed', 'alert')
      } finally {
        commit('setSaving', false)
      }
    },

    // PageSync handler
    handlePageSyncUpdate({ state, dispatch }) {
      if (state.project) {
        dispatch('fetchProject', { id: state.project.id })
      }
    }
  }
}
